import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Tax Filing (IT/GST)",
  "Auditing & Assurance",
  "Company Registration / ROC",
  "Bookkeeping & Accounting",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you. We will respond within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#1A2F24] text-[#FDFBF7] relative grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 relative">
        <div className="lg:col-span-5">
          <div className="overline mb-5 text-[#C5A059]">
            <span className="inline-block w-12 h-px bg-[#C5A059] mr-3 align-middle" />
            Get in touch
          </div>
          <h2
            className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="contact-heading"
          >
            Let's begin with a <em className="italic">conversation.</em>
          </h2>
          <p className="mt-6 text-[#D7D2C6] text-base md:text-lg leading-relaxed max-w-md">
            Share a few details about your need — tax, audit, registration or
            bookkeeping — and we will write back with the next step.
          </p>

          <div className="mt-12 space-y-7 text-[#E8E4D9]">
            <div className="flex gap-4" data-testid="contact-address">
              <MapPin size={20} className="text-[#C5A059] shrink-0 mt-1" />
              <div>
                <div className="overline text-[#C5A059] mb-1">Office</div>
                <address className="not-italic text-[15px] leading-relaxed">
                  3-220/1, Near Sri Rama Bajana Mandir,
                  <br />
                  Brahmin Street, Greamspet,
                  <br />
                  Chittoor, AP — 517002
                </address>
                <a
                  href="https://maps.app.goo.gl/kdeVkvwyJa18ZGS87"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-link"
                  className="inline-block mt-3 text-xs tracking-[0.14em] uppercase text-[#C5A059] hover:text-[#FDFBF7] border-b border-[#C5A059] pb-0.5 transition-colors"
                >
                  View on Google Maps →
                </a>
                <div className="mt-3 text-[13px] text-[#A8B0A9]">
                  Also serving Bengaluru, Chennai, Hyderabad &amp; PAN-India.
                </div>
              </div>
            </div>

            <div className="flex gap-4" data-testid="contact-phone">
              <Phone size={20} className="text-[#C5A059] shrink-0 mt-1" />
              <div>
                <div className="overline text-[#C5A059] mb-1">Telephone</div>
                <a
                  href="tel:+916302568325"
                  className="text-[15px] hover:text-[#C5A059] transition-colors"
                >
                  +91 63025 68325
                </a>
              </div>
            </div>

            <div className="flex gap-4" data-testid="contact-email">
              <Mail size={20} className="text-[#C5A059] shrink-0 mt-1" />
              <div>
                <div className="overline text-[#C5A059] mb-1">Email</div>
                <a
                  href="mailto:roopesh.ca04@gmail.com"
                  className="text-[15px] hover:text-[#C5A059] transition-colors"
                >
                  roopesh.ca04@gmail.com
                </a>
              </div>
            </div>

            <div className="flex gap-4" data-testid="contact-hours">
              <Clock size={20} className="text-[#C5A059] shrink-0 mt-1" />
              <div>
                <div className="overline text-[#C5A059] mb-1">
                  Working Hours
                </div>
                <div className="text-[15px] leading-relaxed">
                  Monday – Saturday · 10:00 AM – 7:00 PM
                  <br />
                  <span className="text-[#A8B0A9]">
                    Sunday by prior appointment
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 bg-[#FDFBF7] text-[#3A453F] p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="overline mb-6 text-[#1A2F24]">
            <span className="divider-gold mr-3" /> Inquiry form
          </div>
          <h3 className="font-serif text-3xl md:text-4xl text-[#111814] leading-tight mb-10">
            Tell us how we can help.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <label className="overline text-[#68766E]">Full Name</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                className="input-line"
                placeholder="Your name"
                data-testid="contact-name-input"
                required
              />
            </div>
            <div>
              <label className="overline text-[#68766E]">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                className="input-line"
                placeholder="you@example.com"
                data-testid="contact-email-input"
                required
              />
            </div>
            <div>
              <label className="overline text-[#68766E]">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="input-line"
                placeholder="+91 XXXXXXXXXX"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <label className="overline text-[#68766E]">Service</label>
              <select
                name="service"
                value={form.service}
                onChange={onChange}
                className="input-line text-[#111814] bg-transparent"
                data-testid="contact-service-select"
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 mt-4">
              <label className="overline text-[#68766E]">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                className="input-line"
                rows={5}
                placeholder="Briefly describe your requirement..."
                data-testid="contact-message-input"
                required
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-[#68766E] max-w-sm">
              Your details remain confidential and are used only to respond to
              this enquiry.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? "Sending..." : "Submit Inquiry"}
              <Send size={14} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
